import { getAccessToken, authDisabled, reauth } from '@/auth/msal';

const BASE = (import.meta.env.VITE_API_BASE_URL as string) || '';
const PREFIX = '/api/v1';

export interface ProblemDetails {
  title: string;
  status: number;
  detail?: unknown;
}

export class ApiError extends Error {
  constructor(public status: number, public problem: ProblemDetails) {
    super(problem.title);
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const token = await getAccessToken();
  // When auth is on but we couldn't get a token, getAccessToken() has already
  // kicked off a re-auth redirect (or one is in progress). Fail this call cleanly
  // instead of sending an unauthenticated request or hanging — the page is
  // navigating to sign-in anyway.
  if (!authDisabled && !token) {
    throw new ApiError(401, { title: 'Signing in…', status: 401 });
  }
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${BASE}${PREFIX}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  // Token expired/rejected mid-session — refresh via redirect (loop-guarded in reauth)
  // rather than leaving the screen blank. The redirect navigates away; the throw is a
  // clean fallback if the guard has stopped further redirects.
  if (res.status === 401 && !authDisabled) {
    await reauth();
    throw new ApiError(401, { title: 'Signing in…', status: 401 });
  }

  if (!res.ok) {
    let problem: ProblemDetails = { title: res.statusText, status: res.status };
    try {
      problem = (await res.json()) as ProblemDetails;
    } catch {
      /* non-JSON error */
    }
    throw new ApiError(res.status, problem);
  }
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get('content-type') ?? '';
  if (!ct.includes('application/json')) {
    // The API always returns JSON. A non-JSON 200 (e.g. the SPA's own index.html
    // served when VITE_API_BASE_URL is unset and /api falls back to the SWA) must
    // NOT be treated as data — otherwise callers get an HTML string and crash on
    // `.map`. Surface it as a clear error instead.
    throw new ApiError(res.status, {
      title: 'Unexpected non-JSON response from API (check VITE_API_BASE_URL / API routing)',
      status: res.status,
      detail: `content-type: ${ct || 'none'}`,
    });
  }
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
  /** Trigger a file download (Excel export). */
  download: async (path: string, filename: string) => {
    const token = await getAccessToken();
    if (!authDisabled && !token) throw new ApiError(401, { title: 'Signing in…', status: 401 });
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(`${BASE}${PREFIX}${path}`, { headers });
    if (!res.ok) throw new ApiError(res.status, { title: 'Export failed', status: res.status });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },
  /** POST a JSON body and save the file the API returns (e.g. a filled-in template). */
  downloadPost: async (path: string, body: unknown, filename: string) => {
    const token = await getAccessToken();
    if (!authDisabled && !token) throw new ApiError(401, { title: 'Signing in…', status: 401 });
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(`${BASE}${PREFIX}${path}`, { method: 'POST', headers, body: JSON.stringify(body) });
    if (!res.ok) {
      const problem = await res.json().catch(() => ({ title: 'Download failed', status: res.status }));
      throw new ApiError(res.status, problem as ProblemDetails);
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },
  /** Upload one file (multipart field "file") and return the JSON response. */
  upload: async <T>(path: string, file: File): Promise<T> => {
    const token = await getAccessToken();
    if (!authDisabled && !token) throw new ApiError(401, { title: 'Signing in…', status: 401 });
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`${BASE}${PREFIX}${path}`, { method: 'POST', headers, body: form });
    const data = await res.json().catch(() => null);
    if (!res.ok) throw new ApiError(res.status, (data as ProblemDetails) ?? { title: 'Upload failed', status: res.status });
    return data as T;
  },
};
